import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * Cascading taxonomy picker. Renders columns left-to-right for each level
 * the user has selected. Returns the final node (deepest selected).
 *
 * Props:
 *   domain: "research_ideas" | "datasets" | "both"
 *   value: taxonomy_id string | null
 *   onChange: (taxonomy_id | null) => void
 */
export default function TaxonomyPicker({ domain = "both", value, onChange }) {
  const [nodes, setNodes] = useState([]);
  const [path, setPath] = useState([]); // selected node ids root → leaf

  useEffect(() => {
    api.get("/taxonomy", { params: { domain } }).then((r) => setNodes(r.data || []));
  }, [domain]);

  // Initialise path from value
  useEffect(() => {
    if (!value || !nodes.length) { setPath([]); return; }
    const ids = [];
    let cur = nodes.find((n) => n.id === value);
    while (cur) {
      ids.unshift(cur.id);
      cur = cur.parent_id ? nodes.find((n) => n.id === cur.parent_id) : null;
    }
    setPath(ids);
  }, [value, nodes]);

  const childrenOf = useMemo(() => {
    const m = new Map();
    for (const n of nodes) {
      const p = n.parent_id || "__root__";
      if (!m.has(p)) m.set(p, []);
      m.get(p).push(n);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
    return m;
  }, [nodes]);

  const setLevel = (level, nodeId) => {
    const next = path.slice(0, level);
    if (nodeId) next.push(nodeId);
    setPath(next);
    onChange(next[next.length - 1] || null);
  };

  // Build column list: root + each selected node's children
  const cols = [{ parentId: "__root__", level: 0 }];
  for (let i = 0; i < path.length; i++) {
    cols.push({ parentId: path[i], level: i + 1 });
  }

  return (
    <div data-testid="taxonomy-picker">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {cols.map((col) => {
          const list = childrenOf.get(col.parentId) || [];
          if (list.length === 0) return null;
          const selectedId = path[col.level] || "";
          return (
            <select
              key={col.level}
              value={selectedId}
              onChange={(e) => setLevel(col.level, e.target.value || null)}
              className="kb-input text-sm min-w-[180px]"
              data-testid={`tax-level-${col.level}`}
            >
              <option value="">— pick {col.level === 0 ? "domain" : `level ${col.level + 1}`} —</option>
              {list.map((n) => (
                <option key={n.id} value={n.id}>{n.name}</option>
              ))}
            </select>
          );
        })}
      </div>
      {path.length > 0 && (
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
          PATH: <span className="text-kb-primary">{path.map((id) => nodes.find((n) => n.id === id)?.name).join(" › ")}</span>
          <button onClick={() => { setPath([]); onChange(null); }} className="ml-3 text-xs text-rose-600 hover:text-rose-700">Clear</button>
        </div>
      )}
    </div>
  );
}
